import React, { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import RolesContext from 'App/views/SchoolDataRoleSelection/RolesContext'
import { SchoolDataRoleSelectionRender } from 'App/views/SchoolDataRoleSelection/SchoolDataRoleSelectionForm'
import {
  SCHOOL_ADD_ADMIN,
  SCHOOL_ADD_DIRECTOR,
  SCHOOL_ADD_TEACHER,
  SCHOOL_REMOVE_ADMIN,
  SCHOOL_REMOVE_COORDINATOR,
  SCHOOL_REMOVE_DIRECTOR,
  SCHOOL_REMOVE_TEACHER,
} from 'graphql/mutations'
import { useForm } from 'hooks'
import PropTypes from 'prop-types'
import UserContext from 'stores/UserStore'

import {
  graphqlClient,
  ROLE_ADMIN_ID,
  ROLE_COORDINATOR_ID,
  ROLE_DIRECTOR_ID,
  ROLE_STATUS_ACTIVE_ID,
  ROLE_STATUS_PENDING_ID,
  ROLE_TEACHER_ID,
} from 'utils'
import { ROUTES } from 'utils/constants'

const DEFAULT_ERROR_MESSAGE = 'Não foi possível fazer o vínculo do perfil. Tente novamente. '

const checkInitialRoleStatus = (checkboxInfo, role, status) =>
  checkboxInfo.some(
    currentCheckbox => currentCheckbox.roleID === role && currentCheckbox.initialValue === status,
  )
const modalRolesDifferentCheckboxValue = (values, checkboxInfo) => {
  const { director, admin } = values
  const tryDirector = director && checkInitialRoleStatus(checkboxInfo, ROLE_DIRECTOR_ID, false)
  const tryAdmin = admin && checkInitialRoleStatus(checkboxInfo, ROLE_ADMIN_ID, false)
  if (tryDirector || tryAdmin) {
    return true
  }
  return false
}
const shouldOpenModal = (values, checkboxInfo) => {
  if (modalRolesDifferentCheckboxValue(values, checkboxInfo)) {
    return true
  }
  return false
}
const showActiveStatus = (currentRoleID, ROLE_ID, school) =>
  (school?.roleStatus?.id === ROLE_STATUS_ACTIVE_ID.toString() ||
    school?.roleStatus?.id === ROLE_STATUS_PENDING_ID.toString()) &&
  currentRoleID === ROLE_ID.toString()

const setInitialRoleStatus = (filteredSchoolRoles, ROLE_ID) =>
  filteredSchoolRoles.some(role => showActiveStatus(role.id, ROLE_ID, role.schools[0]))

const rolesValidate = () => {
  const errors = {}
  return errors
}

const roleMutation = async (status, variables, addRole, removeRole) => {
  if (status && !addRole) {
    return true
  }
  try {
    status ? await addRole(variables) : await removeRole(variables)
    return true
  } catch (err) {
    return false
  }
}

const sendRolesMutations = async (values, variables, useMutations, fields) => {
  const { director, admin, coordinator, teacher } = values
  const { teacher: defaultTeacher } = fields
  const tryTeacher = await roleMutation(
    teacher,
    variables,
    !defaultTeacher ? useMutations.addTeacher : null,
    useMutations.removeTeacher,
  )
  if (!tryTeacher) return false
  const tryCoordinator = await roleMutation(
    coordinator,
    variables,
    null,
    useMutations.removeCoordinator,
  )
  if (!tryCoordinator) return false

  const tryDirector = await roleMutation(
    director,
    variables,
    useMutations.addDirector,
    useMutations.removeDirector,
  )
  if (!tryDirector) return false
  const tryAdmin = await roleMutation(
    admin,
    variables,
    useMutations.addAdmin,
    useMutations.removeAdmin,
  )
  if (!tryAdmin) return false
  return true
}
const SchoolDataRoleSelectionView = ({ currentSchool, filteredSchoolRoles }) => {
  const userStore = useContext(UserContext)
  const [linkToRedirect, setLinkToRedirect] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [graphQLError, setGraphQLError] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const rolesData = useContext(RolesContext)
  const navigate = useNavigate()

  const addDirector = async variables => {
    await graphqlClient().mutate({ mutation: SCHOOL_ADD_DIRECTOR, ...variables })
  }

  const addAdmin = async variables => {
    await graphqlClient().mutate({ mutation: SCHOOL_ADD_ADMIN, ...variables })
  }

  const addTeacher = async variables => {
    await graphqlClient().mutate({ mutation: SCHOOL_ADD_TEACHER, ...variables })
  }

  const removeTeacher = async variables => {
    await graphqlClient().mutate({ mutation: SCHOOL_REMOVE_TEACHER, ...variables })
  }

  const removeCoordinator = async variables => {
    await graphqlClient().mutate({ mutation: SCHOOL_REMOVE_COORDINATOR, ...variables })
  }

  const removeDirector = async variables => {
    await graphqlClient().mutate({ mutation: SCHOOL_REMOVE_DIRECTOR, ...variables })
  }

  const removeAdmin = async variables => {
    await graphqlClient().mutate({ mutation: SCHOOL_REMOVE_ADMIN, ...variables })
  }

  const useMutations = {
    addAdmin,
    addDirector,
    addTeacher,
    removeDirector,
    removeAdmin,
    removeTeacher,
    removeCoordinator,
  }

  const rolesMutationsFeedback = tryRolesMutations => {
    if (values.coordinator && checkInitialRoleStatus(checkboxInfo, ROLE_COORDINATOR_ID, false)) {
      userStore.setNeedSelectProfileCoordinator(true)
    }

    if (!tryRolesMutations) {
      return setGraphQLError(DEFAULT_ERROR_MESSAGE)
    }
    if (values.coordinator && checkInitialRoleStatus(checkboxInfo, ROLE_COORDINATOR_ID, false)) {
      return setLinkToRedirect(ROUTES.PERFIL_DADOS_ESCOLARES_FUNCOES_COORDENADOR)
    }

    return setLinkToRedirect(`${ROUTES.PERFIL_DADOS_ESCOLARES}?redirect=true`)
  }

  const fields = {
    teacher: setInitialRoleStatus(filteredSchoolRoles, ROLE_TEACHER_ID),
    coordinator: setInitialRoleStatus(filteredSchoolRoles, ROLE_COORDINATOR_ID),
    director: setInitialRoleStatus(filteredSchoolRoles, ROLE_DIRECTOR_ID),
    admin: setInitialRoleStatus(filteredSchoolRoles, ROLE_ADMIN_ID),
  }

  const trySubmitRoles = async () => {
    setGraphQLError('')
    setIsSubmitting(true)
    const variables = { variables: { schoolId: +currentSchool.id, levels: [] } }
    const tryRolesMutations = await sendRolesMutations(values, variables, useMutations, fields)
    setIsSubmitting(false)
    rolesMutationsFeedback(tryRolesMutations)
  }

  const onValidForm = values => {
    if (shouldOpenModal(values, checkboxInfo)) {
      return setIsModalOpen(true)
    }
    trySubmitRoles()
  }

  const { values, handleChange, onSubmitForm } = useForm(fields, onValidForm, rolesValidate)

  const checkboxInfo = [
    {
      roleID: ROLE_TEACHER_ID,
      name: 'teacher',
      label: 'Professor',
      value: values.teacher,
      initialValue: fields.teacher,
    },
    {
      roleID: ROLE_COORDINATOR_ID,
      name: 'coordinator',
      label: 'Coordenador',
      value: values.coordinator,
      initialValue: fields.coordinator,
    },
    {
      roleID: ROLE_DIRECTOR_ID,
      name: 'director',
      label: 'Diretor',
      value: values.director,
      initialValue: fields.director,
    },
    {
      roleID: ROLE_ADMIN_ID,
      name: 'admin',
      label: 'Administrador',
      value: values.admin,
      initialValue: fields.admin,
    },
  ]

  const validateRole = id => rolesData.find(role => role.id === id.toString())

  const checkSelection = () => {
    const checkboxValues = Object.values(values)
    const disableSubmit = !checkboxValues.includes(true)
    return disableSubmit
  }

  if (linkToRedirect) {
    navigate(linkToRedirect)
  }

  return (
    <SchoolDataRoleSelectionRender
      onSubmitForm={onSubmitForm}
      checkboxInfo={checkboxInfo}
      validateRole={validateRole}
      handleChange={handleChange}
      graphQLError={graphQLError}
      checkSelection={checkSelection}
      setLinkToRedirect={setLinkToRedirect}
      isSubmitting={isSubmitting}
      isModalOpen={isModalOpen}
      setIsModalOpen={setIsModalOpen}
      trySubmitRoles={trySubmitRoles}
      currentSchool={currentSchool}
    />
  )
}
SchoolDataRoleSelectionView.propTypes = {
  currentSchool: PropTypes.object,
  filteredSchoolRoles: PropTypes.array,
}

export default SchoolDataRoleSelectionView
